import type {
	FinancialPropertyListItem,
	ServicePeriodListItem,
} from "@/features/financialShared";
import {
	ServicePeriodStatus,
	toApiSortOrder,
} from "@/features/financialShared";
import { inferUploadContentType } from "@/features/upload/inferUploadContentType";
import type {
	ChargesApproverOption,
	ChargesManagementPermissions,
	ChargesManagementPropertiesResponse,
	ChargesPeriodFileBody,
	ChargesServicePeriodsResponse,
	CreateChargesServicePeriodBody,
	GetChargesManagementPropertiesArgs,
	GetChargesServicePeriodsArgs,
} from "./chargesManagement.api.types";
import { mapChargesStatusFromApi } from "./chargesManagement.api.types";

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function asString(value: unknown, fallback = ""): string {
	return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
	if (typeof value === "boolean") return value;
	return fallback;
}

function unwrapData(raw: unknown): Record<string, unknown> {
	const root = asRecord(raw) ?? {};
	return asRecord(root.data) ?? root;
}

function formatShortDate(value: string): string {
	const trimmed = value.trim();
	if (!trimmed) return "";
	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) return trimmed;
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function parseApproverOptions(raw: unknown): ChargesApproverOption[] {
	if (!Array.isArray(raw)) return [];
	return raw
		.map((item) => {
			const row = asRecord(item);
			if (!row) return null;
			const id = asString(row.id).trim();
			const name = asString(row.name).trim();
			if (!id && !name) return null;
			return { id: id || name, name: name || id };
		})
		.filter((item): item is ChargesApproverOption => Boolean(item));
}

function parseNamedPerson(raw: unknown): string {
	if (typeof raw === "string") return raw.trim();
	const row = asRecord(raw);
	if (!row) return "";
	return asString(row.name).trim();
}

function parseLatestPeriodLabel(raw: unknown): string {
	if (!raw) return "—";
	if (typeof raw === "string") return raw.trim() || "—";
	const row = asRecord(raw);
	if (!row) return "—";
	return asString(row.label).trim() || "—";
}

function parseChargesCategories(raw: unknown): string[] {
	if (typeof raw === "string") {
		return raw
			.split(",")
			.map((entry) => entry.trim())
			.filter(Boolean);
	}
	if (!Array.isArray(raw)) return [];
	return raw
		.map((item) => {
			if (typeof item === "string") return item.trim();
			const row = asRecord(item);
			return asString(row?.name).trim();
		})
		.filter(Boolean);
}

function parsePropertyItem(raw: unknown): FinancialPropertyListItem | null {
	const row = asRecord(raw);
	if (!row) return null;
	const id = asString(row.propertyId ?? row.id).trim();
	if (!id) return null;
	const name = asString(row.propertyName ?? row.name).trim();
	return {
		id,
		name: name || "Untitled property",
		totalUnits: asNumber(row.totalUnits),
		latestServicePeriodLabel: parseLatestPeriodLabel(row.latestServicePeriod),
		status: mapChargesStatusFromApi(row.status),
		lastUpdatedDate:
			asString(row.lastUpdatedDisplay).trim() ||
			formatShortDate(asString(row.lastUpdatedDate)) ||
			"—",
		approver: parseNamedPerson(row.approver) || "—",
		chargesCategories: parseChargesCategories(row.chargesCategories),
	};
}

export function parseChargesManagementPropertiesResponse(
	raw: unknown,
): ChargesManagementPropertiesResponse {
	const data = unwrapData(raw);
	const items = Array.isArray(data.items)
		? data.items
				.map(parsePropertyItem)
				.filter((item): item is FinancialPropertyListItem => Boolean(item))
		: [];
	const page = Math.max(1, asNumber(data.page, 1));
	const pageSize = Math.max(1, asNumber(data.pageSize, 10));
	const total = asNumber(data.total, items.length);
	const totalPages = Math.max(1, asNumber(data.totalPages, 1));
	const lastSyncDisplay = asString(data.lastSyncDisplay).trim();
	const permissions = asRecord(data.permissions) ?? {};
	return {
		pageTitle: asString(data.pageTitle, "Charges Management"),
		lastSyncLabel: lastSyncDisplay ? `Last Sync: ${lastSyncDisplay}` : "",
		approverOptions: parseApproverOptions(data.approverOptions),
		items,
		page,
		pageSize,
		total,
		totalPages,
		hasPrevious: asBoolean(data.hasPrevious, page > 1),
		hasNext: asBoolean(data.hasNext, page < totalPages),
		permissions: {
			canEdit: asBoolean(permissions.canEdit, true),
			canPublish: asBoolean(permissions.canPublish, false),
		} satisfies ChargesManagementPermissions,
	};
}

function parsePeriodItem(
	raw: unknown,
	propertyId: string,
): ServicePeriodListItem | null {
	const row = asRecord(raw);
	if (!row) return null;
	const id = asString(row.id).trim();
	if (!id) return null;
	const label = asString(row.label).trim();
	const paidUnits = asNumber(row.paidUnits);
	const totalUnits = asNumber(row.totalUnits);
	const lastUpdatedBy =
		parseNamedPerson(row.lastUpdatedBy) || parseNamedPerson(row.approver);
	return {
		id,
		propertyId: asString(row.propertyId, propertyId),
		tabId: asString(row.tabId).trim() || undefined,
		periodLabel: label || "Untitled period",
		dueDate:
			formatShortDate(asString(row.dueDate)) || asString(row.dueDate) || "—",
		paidUnitsLabel:
			totalUnits > 0 ? `${paidUnits} / ${totalUnits}` : String(paidUnits),
		status: mapChargesStatusFromApi(row.status) ?? ServicePeriodStatus.DRAFT,
		createdDate: formatShortDate(asString(row.createdAt)) || "—",
		lastUpdatedBy: lastUpdatedBy || "—",
		lastUpdatedDate: formatShortDate(asString(row.updatedAt)) || "—",
		approver: parseNamedPerson(row.approver) || "—",
		isUploaded:
			row.hasUploadedData == null && row.isUploaded == null
				? undefined
				: asBoolean(row.hasUploadedData ?? row.isUploaded, false),
	};
}

export function parseChargesServicePeriodsResponse(
	raw: unknown,
	propertyId: string,
): ChargesServicePeriodsResponse {
	const data = unwrapData(raw);
	const resolvedPropertyId = asString(data.propertyId, propertyId);
	const items = Array.isArray(data.items)
		? data.items
				.map((item) => parsePeriodItem(item, resolvedPropertyId))
				.filter((item): item is ServicePeriodListItem => Boolean(item))
		: [];
	const permissions = asRecord(data.permissions) ?? {};
	return {
		propertyId: resolvedPropertyId,
		propertyName: asString(data.propertyName).trim(),
		unitCount: asNumber(data.unitCount),
		items,
		approverOptions: parseApproverOptions(data.approverOptions),
		page: Math.max(1, asNumber(data.page, 1)),
		pageSize: Math.max(1, asNumber(data.pageSize, 10)),
		total: asNumber(data.total, items.length),
		totalPages: Math.max(1, asNumber(data.totalPages, 1)),
		emptyMessage:
			asString(data.emptyMessage).trim() || "No service periods found.",
		canCreate: asBoolean(permissions.canCreate ?? data.canCreate, true),
		canPublish: asBoolean(permissions.canPublish ?? data.canPublish, false),
	};
}

export function buildChargesManagementListParams(
	args: GetChargesManagementPropertiesArgs,
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		status: args.status?.trim() || "ALL",
		sortBy: args.sortBy?.trim() || "NAME",
		sortOrder: toApiSortOrder(args.sortOrder),
		page: args.page ?? 1,
		pageSize: args.pageSize ?? 10,
	};
	if (args.search?.trim()) params.search = args.search.trim();
	if (args.approverId?.trim()) params.approverId = args.approverId.trim();
	return params;
}

export function buildChargesServicePeriodListParams(
	args: GetChargesServicePeriodsArgs,
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		page: args.page ?? 1,
		pageSize: args.pageSize ?? 10,
		status: args.status?.trim() || "ALL",
	};
	if (args.search?.trim()) params.search = args.search.trim();
	if (args.approverId?.trim()) params.approverId = args.approverId.trim();
	if (args.tabId?.trim()) params.tabId = args.tabId.trim();
	return params;
}

export {
	parseDownloadedFilename,
	triggerBrowserDownload,
} from "@/features/financialShared";

export function spreadsheetMimeType(file: File): string {
	return (
		inferUploadContentType(file) ||
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
	);
}

export function toChargesPeriodFileBody(
	file: File,
	filePath: string,
): ChargesPeriodFileBody {
	return {
		filePath,
		fileName: file.name,
		mimeType: spreadsheetMimeType(file),
		fileSize: file.size,
	};
}

export function buildCreateChargesServicePeriodBody(payload: {
	tabId: string;
	startDate: string;
	endDate: string;
	dueDate: string;
	file?: ChargesPeriodFileBody;
}): CreateChargesServicePeriodBody {
	return {
		tabId: payload.tabId,
		periodStart: payload.startDate,
		periodEnd: payload.endDate,
		dueDate: payload.dueDate,
		...(payload.file ? { file: payload.file } : {}),
	};
}
